import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  BadgeCheck,
  Building2,
  CalendarClock,
  Check,
  CircleDollarSign,
  Coins,
  Gift,
  Landmark,
  LockKeyhole,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  WalletCards,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  activateMeroCreditRule,
  createMeroCreditPurchase,
  createMeroCreditRule,
  fetchBusinessMeroCredits,
  fetchCustomerMeroCredits,
  fetchMeroCreditConfiguration,
  fetchMeroCreditDashboard,
  fetchMeroCreditRules,
  fetchMeroCreditSettlements,
  messageFromError,
  pauseMeroCreditRule,
  updateMeroCreditConfiguration,
  type MeroCreditActivity,
  type MeroCreditConfiguration,
  type MeroCreditRule,
  type MeroCreditWalletData,
} from "../api";
import { AppAlert } from "../components/AppAlertDialog";

const credits = (minor = 0) =>
  new Intl.NumberFormat("en-NP", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);

const date = (value: string | null | undefined) =>
  value
    ? new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" }).format(
        new Date(value),
      )
    : "—";

function Loading({ label }: { label: string }) {
  return (
    <div className="mc-state" role="status">
      <span className="spinner" />
      <strong>{label}</strong>
    </div>
  );
}

function Failure({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <>
      <AppAlert message={messageFromError(error)} />
      <div className="mc-state mc-state--error">
        <AlertTriangle />
        <strong>We could not open Mero Credits</strong>
        <p>Try loading this area again.</p>
        <button className="button button--outline" type="button" onClick={retry}>
          <RefreshCw /> Try again
        </button>
      </div>
    </>
  );
}

function CreditBalance({
  data,
  audience,
}: {
  data: MeroCreditWalletData;
  audience: "customer" | "business";
}) {
  return (
    <>
      <section className="mc-balance-card">
        <div className="mc-balance-card__copy">
          <p className="eyebrow">MERO CREDITS</p>
          <h1>{credits(data.wallet.totalBalanceMinor)}</h1>
          <p>
            {audience === "customer"
              ? "Your platform-wide credit balance"
              : "Your business incentive balance"}
          </p>
          <span
            className={`mc-wallet-status mc-wallet-status--${data.wallet.status.toLowerCase()}`}
          >
            <i /> {data.wallet.status}
          </span>
        </div>
        <div className="mc-card-art" aria-hidden="true">
          <img src="/brand/mero-brand-logo.png" alt="" />
          <Sparkles />
        </div>
      </section>
      <div className="mc-metric-grid">
        <article>
          <span>
            <Landmark />
          </span>
          <div>
            <small>Purchased credits</small>
            <strong>{credits(data.breakdown.purchasedMinor)}</strong>
            <p>Refund rules apply</p>
          </div>
        </article>
        <article>
          <span>
            <Gift />
          </span>
          <div>
            <small>Promotional credits</small>
            <strong>{credits(data.breakdown.promotionalMinor)}</strong>
            <p>Campaign and welcome value</p>
          </div>
        </article>
        <article>
          <span>
            <CalendarClock />
          </span>
          <div>
            <small>Expiring in 30 days</small>
            <strong>{credits(data.breakdown.expiringSoonMinor)}</strong>
            <p>
              {data.breakdown.nextExpiryAt
                ? `Next ${date(data.breakdown.nextExpiryAt)}`
                : "Nothing expiring soon"}
            </p>
          </div>
        </article>
      </div>
    </>
  );
}

function PurchasePanel({
  data,
  audience,
}: {
  data: MeroCreditWalletData;
  audience: "customer" | "business";
}) {
  const queryClient = useQueryClient();
  const [amountMinor, setAmountMinor] = useState(
    data.configuration.allowedDenominationsMinor[0] ?? data.configuration.minimumPurchaseMinor,
  );
  const [providerCode, setProviderCode] = useState(data.configuration.approvedProviders[0] ?? "");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const purchase = useMutation({
    mutationFn: () =>
      createMeroCreditPurchase(audience, {
        amountMinor,
        providerCode,
        purpose:
          audience === "business"
            ? "Business incentive wallet funding"
            : "Personal Mero Credits wallet",
      }),
    onSuccess: async () => {
      setError("");
      setNotice(
        "Purchase order created. Credits will appear only after the approved provider confirms payment.",
      );
      await queryClient.invalidateQueries({ queryKey: ["mero-credits", audience] });
    },
    onError: (caught) => {
      setNotice("");
      setError(messageFromError(caught));
    },
  });
  const enabled =
    data.configuration.regulatoryState === "APPROVED" && data.configuration.purchaseEnabled;
  return (
    <section className="mc-panel">
      <div className="mc-panel__heading">
        <div>
          <p className="eyebrow">BUY MERO CREDITS</p>
          <h2>Top up with verified payment</h2>
          <p>Purchased value remains separately auditable from promotional credits.</p>
        </div>
        <CircleDollarSign />
      </div>
      {!enabled ? (
        <div className="mc-gate">
          <LockKeyhole />
          <div>
            <strong>Purchasing is not launched yet</strong>
            <p>
              Platform Admin must record regulatory approval and enable an approved payment
              provider. Your earned promotional credits remain available.
            </p>
          </div>
        </div>
      ) : (
        <form
          className="mc-purchase-form"
          onSubmit={(event) => {
            event.preventDefault();
            purchase.mutate();
          }}
        >
          <fieldset>
            <legend>Choose an amount</legend>
            <div className="mc-denominations">
              {data.configuration.allowedDenominationsMinor.map((amount) => (
                <button
                  className={amountMinor === amount ? "is-selected" : ""}
                  type="button"
                  key={amount}
                  onClick={() => {
                    setAmountMinor(amount);
                  }}
                >
                  <span>{credits(amount)}</span>
                  {amountMinor === amount && <Check />}
                </button>
              ))}
            </div>
          </fieldset>
          <label>
            <span>Approved payment provider</span>
            <select
              required
              value={providerCode}
              onChange={(event) => {
                setProviderCode(event.target.value);
              }}
            >
              {data.configuration.approvedProviders.map((provider) => (
                <option value={provider} key={provider}>
                  {provider}
                </option>
              ))}
            </select>
          </label>
          <button
            className="button button--primary"
            disabled={purchase.isPending || !providerCode}
            type="submit"
          >
            {purchase.isPending ? "Creating order…" : `Buy ${credits(amountMinor)} Mero Credits`} <ArrowUpRight />
          </button>
        </form>
      )}
      {error && (
        <AppAlert
          message={error}
          onDismiss={() => {
            setError("");
          }}
        />
      )}
      {notice && (
        <p className="mc-inline-notice" role="status">
          {notice}
        </p>
      )}
    </section>
  );
}

function ActivityList({ activity }: { activity: MeroCreditActivity[] }) {
  return (
    <section className="mc-panel">
      <div className="mc-panel__heading">
        <div>
          <p className="eyebrow">APPEND-ONLY LEDGER</p>
          <h2>Credit activity</h2>
          <p>
            Every grant, purchase, redemption, expiry, and reversal keeps a permanent audit trail.
          </p>
        </div>
        <Coins />
      </div>
      {activity.length ? (
        <div className="mc-activity">
          {activity.map((item) => {
            const positive = item.amountMinor > 0;
            return (
              <article key={item.publicId}>
                <span className={positive ? "is-positive" : "is-negative"}>
                  {positive ? <ArrowDownLeft /> : <ArrowUpRight />}
                </span>
                <div>
                  <strong>{item.entryType.replaceAll("_", " ")}</strong>
                  <small>
                    {item.merchantName ?? item.ruleCode ?? item.fundingBucket.replaceAll("_", " ")}{" "}
                    · {date(item.createdAt)}
                  </small>
                </div>
                <b className={positive ? "is-positive" : "is-negative"}>
                  {positive ? "+" : "−"}
                  {credits(Math.abs(item.amountMinor))}
                </b>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="mc-empty">
          <Coins />
          <strong>Your ledger is ready</strong>
          <p>Credit activity will appear here.</p>
        </div>
      )}
    </section>
  );
}

export function CustomerMeroCredits() {
  const query = useQuery({
    queryKey: ["mero-credits", "customer"],
    queryFn: fetchCustomerMeroCredits,
  });
  if (query.isLoading) return <Loading label="Opening your Mero Credits wallet" />;
  if (query.isError || !query.data)
    return (
      <Failure
        error={query.error}
        retry={() => {
          void query.refetch();
        }}
      />
    );
  const data = query.data;
  return (
    <main className="mc-page">
      <header className="mc-page-heading">
        <div>
          <p className="eyebrow">ONE PLATFORM WALLET</p>
          <h1>Mero Credits</h1>
          <p>{data.separationNotice}</p>
        </div>
        <span>
          <WalletCards />
        </span>
      </header>
      <CreditBalance data={data} audience="customer" />
      <div className="mc-two-column">
        <PurchasePanel data={data} audience="customer" />
        <section className="mc-panel">
          <div className="mc-panel__heading">
            <div>
              <p className="eyebrow">WHERE TO USE THEM</p>
              <h2>Participating businesses</h2>
              <p>
                Cross-merchant redemption is shown only after each merchant accepts the program.
              </p>
            </div>
            <Building2 />
          </div>
          {data.acceptedMerchants.length ? (
            <div className="mc-merchants">
              {data.acceptedMerchants.map((merchant) => (
                <article key={merchant.publicId}>
                  <span>
                    {merchant.logoUrl ? (
                      <img src={merchant.logoUrl} alt="" />
                    ) : (
                      merchant.businessName.slice(0, 2).toUpperCase()
                    )}
                  </span>
                  <div>
                    <strong>{merchant.businessName}</strong>
                    <small>
                      Up to {String(merchant.maximumPaymentBasisPoints / 100)}% of an eligible
                      basket
                    </small>
                  </div>
                  <BadgeCheck />
                </article>
              ))}
            </div>
          ) : (
            <div className="mc-gate mc-gate--plain">
              <ShieldCheck />
              <div>
                <strong>Merchant rollout is controlled</strong>
                <p>
                  No business is accepting platform-wide credits yet. Your balance stays safe and
                  separate.
                </p>
              </div>
            </div>
          )}
        </section>
      </div>
      <ActivityList activity={data.activity} />
    </main>
  );
}

export function BusinessMeroCredits() {
  const query = useQuery({
    queryKey: ["mero-credits", "business"],
    queryFn: fetchBusinessMeroCredits,
  });
  if (query.isLoading) return <Loading label="Opening the business Mero Credits wallet" />;
  if (query.isError || !query.data)
    return (
      <Failure
        error={query.error}
        retry={() => {
          void query.refetch();
        }}
      />
    );
  const data = query.data;
  return (
    <main className="portal-content mc-page mc-page--portal">
      <header className="welcome-row">
        <div>
          <p className="eyebrow">PLATFORM INCENTIVE WALLET</p>
          <h1>Business Mero Credits</h1>
          <p>
            Manage platform-funded incentive value without changing your Reward Points strategy.
          </p>
        </div>
        <span className={`mc-acceptance ${data.acceptance?.enabled ? "is-on" : ""}`}>
          <i /> Merchant acceptance {data.acceptance?.enabled ? "enabled" : "disabled"}
        </span>
      </header>
      <CreditBalance data={data} audience="business" />
      <div className="mc-two-column">
        <PurchasePanel data={data} audience="business" />
        <section className="mc-panel">
          <div className="mc-panel__heading">
            <div>
              <p className="eyebrow">GUARDRAILS</p>
              <h2>Approved business use</h2>
            </div>
            <ShieldCheck />
          </div>
          <div className="mc-use-list">
            <div>
              <strong>Permitted</strong>
              {(data.permittedUses ?? []).map((item) => (
                <p key={item}>
                  <Check /> {item}
                </p>
              ))}
            </div>
            <div className="is-prohibited">
              <strong>Never permitted</strong>
              {(data.prohibitedUses ?? []).map((item) => (
                <p key={item}>
                  <LockKeyhole /> {item}
                </p>
              ))}
            </div>
          </div>
        </section>
      </div>
      <ActivityList activity={data.activity} />
      <section className="mc-panel">
        <div className="mc-panel__heading">
          <div>
            <p className="eyebrow">MERCHANT RECEIVABLES</p>
            <h2>Settlement activity</h2>
            <p>Confirmed customer redemptions create traceable receivables.</p>
          </div>
          <Landmark />
        </div>
        {(data.settlements ?? []).length ? (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Redemption</th>
                  <th>Gross</th>
                  <th>Net</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {(data.settlements ?? []).map((item) => (
                  <tr key={item.publicId}>
                    <td>{item.redemptionPublicId.slice(0, 8)}</td>
                    <td>{credits(item.grossAmountMinor)}</td>
                    <td>{credits(item.netAmountMinor)}</td>
                    <td>
                      <span className={`status-pill status-pill--${item.status.toLowerCase()}`}>
                        {item.status}
                      </span>
                    </td>
                    <td>{date(item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mc-empty">
            <Landmark />
            <strong>No settlements yet</strong>
            <p>Eligible redemption receivables will appear here.</p>
          </div>
        )}
      </section>
    </main>
  );
}

export function MeroCreditConfigurationForm({
  configuration,
}: {
  configuration: MeroCreditConfiguration;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    regulatoryState: configuration.regulatoryState,
    purchasingEnabled: configuration.purchasingEnabled ?? false,
    customerPurchasingEnabled: configuration.customerPurchasingEnabled ?? false,
    businessPurchasingEnabled: configuration.businessPurchasingEnabled ?? false,
    globalRedemptionEnabled: configuration.globalRedemptionEnabled,
    minimumPurchase: String(configuration.minimumPurchaseMinor / 100),
    maximumPurchase: String(configuration.maximumPurchaseMinor / 100),
    dailyLimit: String((configuration.dailyPurchaseLimitMinor ?? 25_000_000) / 100),
    monthlyLimit: String((configuration.monthlyPurchaseLimitMinor ?? 100_000_000) / 100),
    denominations: configuration.allowedDenominationsMinor
      .map((value) => String(value / 100))
      .join(", "),
    customAmountEnabled: configuration.customAmountEnabled,
    providers: configuration.approvedProviders.join(", "),
    refundMode: configuration.refundMode,
    purchasedExpiryDays:
      configuration.purchasedExpiryDays === null ? "" : String(configuration.purchasedExpiryDays),
    promotionalExpiryDays: String(configuration.promotionalDefaultExpiryDays ?? 90),
    dualApprovalThreshold: String(
      (configuration.adjustmentDualApprovalThresholdMinor ?? 100_000) / 100,
    ),
    approvalReference: configuration.regulatoryApprovalReference ?? "",
  });
  const [error, setError] = useState("");
  useEffect(() => {
    setError("");
  }, [configuration.updatedAt]);
  const update = useMutation({
    mutationFn: () =>
      updateMeroCreditConfiguration({
        regulatoryState: form.regulatoryState,
        purchasingEnabled: form.purchasingEnabled,
        customerPurchasingEnabled: form.customerPurchasingEnabled,
        businessPurchasingEnabled: form.businessPurchasingEnabled,
        globalRedemptionEnabled: form.globalRedemptionEnabled,
        minimumPurchaseMinor: Math.round(Number(form.minimumPurchase) * 100),
        maximumPurchaseMinor: Math.round(Number(form.maximumPurchase) * 100),
        dailyPurchaseLimitMinor: Math.round(Number(form.dailyLimit) * 100),
        monthlyPurchaseLimitMinor: Math.round(Number(form.monthlyLimit) * 100),
        allowedDenominationsMinor: form.denominations
          .split(",")
          .map((value) => Math.round(Number(value.trim()) * 100))
          .filter((value) => value > 0),
        customAmountEnabled: form.customAmountEnabled,
        approvedProviders: form.providers
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
        refundMode: form.refundMode,
        purchasedExpiryDays: form.purchasedExpiryDays ? Number(form.purchasedExpiryDays) : null,
        promotionalDefaultExpiryDays: Number(form.promotionalExpiryDays),
        adjustmentDualApprovalThresholdMinor: Math.round(Number(form.dualApprovalThreshold) * 100),
        regulatoryApprovalReference: form.approvalReference.trim() || null,
      }),
    onSuccess: async () => {
      setError("");
      await queryClient.invalidateQueries({ queryKey: ["mero-credits", "admin"] });
    },
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  const toggle = (
    key:
      | "purchasingEnabled"
      | "customerPurchasingEnabled"
      | "businessPurchasingEnabled"
      | "globalRedemptionEnabled"
      | "customAmountEnabled",
  ) => {
    setForm((current) => ({ ...current, [key]: !current[key] }));
  };
  return (
    <form
      className="mc-config"
      onSubmit={(event) => {
        event.preventDefault();
        update.mutate();
      }}
    >
      {error && (
        <AppAlert
          message={error}
          onDismiss={() => {
            setError("");
          }}
        />
      )}
      <div
        className={`mc-regulatory-state ${form.regulatoryState === "APPROVED" ? "is-approved" : ""}`}
      >
        <ShieldCheck />
        <div>
          <strong>Regulatory launch state</strong>
          <p>
            Purchasing and global redemption stay technically blocked unless this is APPROVED with
            an auditable reference.
          </p>
        </div>
        <select
          value={form.regulatoryState}
          onChange={(event) => {
            setForm((current) => ({
              ...current,
              regulatoryState: event.target.value as "BLOCKED" | "APPROVED",
            }));
          }}
        >
          <option value="BLOCKED">BLOCKED</option>
          <option value="APPROVED">APPROVED</option>
        </select>
      </div>
      <div className="mc-switch-grid">
        {(
          [
            ["purchasingEnabled", "Master purchasing"],
            ["customerPurchasingEnabled", "Customer purchasing"],
            ["businessPurchasingEnabled", "Business purchasing"],
            ["globalRedemptionEnabled", "Cross-merchant redemption"],
            ["customAmountEnabled", "Custom purchase amounts"],
          ] as const
        ).map(([key, label]) => (
          <label className="mc-switch" key={key}>
            <input
              type="checkbox"
              checked={form[key]}
              onChange={() => {
                toggle(key);
              }}
            />
            <span>
              <i />
            </span>
            <b>{label}</b>
          </label>
        ))}
      </div>
      <div className="mc-form-grid">
        <label>
          <span>Approval reference</span>
          <input
            value={form.approvalReference}
            onChange={(event) => {
              setForm((current) => ({ ...current, approvalReference: event.target.value }));
            }}
            placeholder="Regulator / counsel reference"
          />
        </label>
        <label>
          <span>Approved providers</span>
          <input
            value={form.providers}
            onChange={(event) => {
              setForm((current) => ({ ...current, providers: event.target.value }));
            }}
            placeholder="Provider codes, comma separated"
          />
        </label>
        <label>
          <span>Minimum credit purchase</span>
          <input
            type="number"
            min="1"
            value={form.minimumPurchase}
            onChange={(event) => {
              setForm((current) => ({ ...current, minimumPurchase: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Maximum credit purchase</span>
          <input
            type="number"
            min="1"
            value={form.maximumPurchase}
            onChange={(event) => {
              setForm((current) => ({ ...current, maximumPurchase: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Daily credit limit</span>
          <input
            type="number"
            min="1"
            value={form.dailyLimit}
            onChange={(event) => {
              setForm((current) => ({ ...current, dailyLimit: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Monthly credit limit</span>
          <input
            type="number"
            min="1"
            value={form.monthlyLimit}
            onChange={(event) => {
              setForm((current) => ({ ...current, monthlyLimit: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Credit denominations</span>
          <input
            value={form.denominations}
            onChange={(event) => {
              setForm((current) => ({ ...current, denominations: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Refund mode</span>
          <select
            value={form.refundMode}
            onChange={(event) => {
              setForm((current) => ({
                ...current,
                refundMode: event.target.value as typeof current.refundMode,
              }));
            }}
          >
            <option value="DISABLED">Disabled</option>
            <option value="UNUSED_ONLY">Unused purchased credits only</option>
            <option value="PARTIAL_CONTROLLED">Controlled partial refunds</option>
          </select>
        </label>
        <label>
          <span>Purchased expiry days (blank = never)</span>
          <input
            type="number"
            min="1"
            value={form.purchasedExpiryDays}
            onChange={(event) => {
              setForm((current) => ({ ...current, purchasedExpiryDays: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Promotional expiry days</span>
          <input
            type="number"
            min="1"
            value={form.promotionalExpiryDays}
            onChange={(event) => {
              setForm((current) => ({ ...current, promotionalExpiryDays: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Dual approval above (Mero Credits)</span>
          <input
            type="number"
            min="1"
            value={form.dualApprovalThreshold}
            onChange={(event) => {
              setForm((current) => ({ ...current, dualApprovalThreshold: event.target.value }));
            }}
          />
        </label>
      </div>
      <button className="button button--primary" disabled={update.isPending} type="submit">
        <ShieldCheck /> {update.isPending ? "Saving controls…" : "Save governed configuration"}
      </button>
    </form>
  );
}

function RuleBuilder() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    code: "",
    name: "",
    description: "",
    audience: "CUSTOMER" as "CUSTOMER" | "BUSINESS" | "BOTH",
    triggerType: "FIRST_QUALIFIED_PURCHASE" as const,
    award: "5",
    fundingBucket: "PROMOTIONAL" as const,
    budget: "",
    expiryDays: "90",
  });
  const [error, setError] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      createMeroCreditRule({
        code: form.code
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9_]/g, "_"),
        name: form.name,
        description: form.description,
        audience: form.audience,
        triggerType: form.triggerType,
        awardMinor: Math.round(Number(form.award) * 100),
        fundingBucket: form.fundingBucket,
        totalBudgetMinor: form.budget ? Math.round(Number(form.budget) * 100) : null,
        expiryDays: Number(form.expiryDays),
      }),
    onSuccess: async () => {
      setForm((value) => ({ ...value, code: "", name: "", description: "" }));
      setError("");
      await queryClient.invalidateQueries({ queryKey: ["mero-credits", "admin", "rules"] });
    },
    onError: (caught) => {
      setError(messageFromError(caught));
    },
  });
  return (
    <form
      className="mc-rule-form"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      {error && (
        <AppAlert
          message={error}
          onDismiss={() => {
            setError("");
          }}
        />
      )}
      <div className="mc-form-grid">
        <label>
          <span>Rule code</span>
          <input
            required
            minLength={3}
            value={form.code}
            onChange={(event) => {
              setForm((current) => ({ ...current, code: event.target.value }));
            }}
            placeholder="FIRST_PURCHASE_2026"
          />
        </label>
        <label>
          <span>Rule name</span>
          <input
            required
            minLength={3}
            value={form.name}
            onChange={(event) => {
              setForm((current) => ({ ...current, name: event.target.value }));
            }}
          />
        </label>
        <label className="mc-form-wide">
          <span>Description</span>
          <textarea
            required
            minLength={10}
            rows={3}
            value={form.description}
            onChange={(event) => {
              setForm((current) => ({ ...current, description: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Audience</span>
          <select
            value={form.audience}
            onChange={(event) => {
              setForm((current) => ({
                ...current,
                audience: event.target.value as typeof current.audience,
              }));
            }}
          >
            <option>CUSTOMER</option>
            <option>BUSINESS</option>
            <option>BOTH</option>
          </select>
        </label>
        <label>
          <span>Award (Mero Credits)</span>
          <input
            required
            min="0.01"
            step="0.01"
            type="number"
            value={form.award}
            onChange={(event) => {
              setForm((current) => ({ ...current, award: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Total budget (Mero Credits, optional)</span>
          <input
            min="0.01"
            step="0.01"
            type="number"
            value={form.budget}
            onChange={(event) => {
              setForm((current) => ({ ...current, budget: event.target.value }));
            }}
          />
        </label>
        <label>
          <span>Credit expiry (days)</span>
          <input
            required
            min="1"
            type="number"
            value={form.expiryDays}
            onChange={(event) => {
              setForm((current) => ({ ...current, expiryDays: event.target.value }));
            }}
          />
        </label>
      </div>
      <button className="button button--primary" disabled={mutation.isPending} type="submit">
        <Plus /> {mutation.isPending ? "Creating draft…" : "Create draft rule"}
      </button>
    </form>
  );
}

function Rules({ rules }: { rules: MeroCreditRule[] }) {
  const queryClient = useQueryClient();
  const transition = useMutation({
    mutationFn: ({ rule, action }: { rule: MeroCreditRule; action: "activate" | "pause" }) =>
      action === "activate"
        ? activateMeroCreditRule(rule.publicId)
        : pauseMeroCreditRule(rule.publicId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mero-credits", "admin", "rules"] });
    },
  });
  return (
    <div className="mc-rule-list">
      {rules.map((rule) => (
        <article key={rule.publicId}>
          <span className={`status-pill status-pill--${rule.status.toLowerCase()}`}>
            {rule.status}
          </span>
          <div>
            <strong>{rule.name}</strong>
            <p>{rule.description}</p>
            <small>
              {rule.code} · {rule.audience} · {rule.triggerType.replaceAll("_", " ")}
            </small>
          </div>
          <div className="mc-rule-list__award">
            <strong>{credits(rule.awardMinor)}</strong>
            <small>
              {rule.fundingBucket.replaceAll("_", " ")} · v{String(rule.versionNumber)}
            </small>
            {rule.status === "DRAFT" ? (
              <button
                className="button button--outline button--compact"
                disabled={transition.isPending}
                type="button"
                onClick={() => {
                  transition.mutate({ rule, action: "activate" });
                }}
              >
                Activate
              </button>
            ) : rule.status === "ACTIVE" ? (
              <button
                className="button button--quiet button--compact"
                disabled={transition.isPending}
                type="button"
                onClick={() => {
                  transition.mutate({ rule, action: "pause" });
                }}
              >
                Pause
              </button>
            ) : null}
          </div>
        </article>
      ))}
    </div>
  );
}

export function PlatformMeroCredits() {
  const dashboard = useQuery({
    queryKey: ["mero-credits", "admin", "dashboard"],
    queryFn: fetchMeroCreditDashboard,
  });
  const configuration = useQuery({
    queryKey: ["mero-credits", "admin", "configuration"],
    queryFn: fetchMeroCreditConfiguration,
  });
  const rules = useQuery({
    queryKey: ["mero-credits", "admin", "rules"],
    queryFn: fetchMeroCreditRules,
  });
  const settlements = useQuery({
    queryKey: ["mero-credits", "admin", "settlements"],
    queryFn: fetchMeroCreditSettlements,
  });
  if (dashboard.isLoading || configuration.isLoading || rules.isLoading)
    return <Loading label="Opening Mero Credits control centre" />;
  if (
    dashboard.isError ||
    configuration.isError ||
    rules.isError ||
    !dashboard.data ||
    !configuration.data ||
    !rules.data
  )
    return (
      <Failure
        error={dashboard.error ?? configuration.error ?? rules.error}
        retry={() => {
          void Promise.all([dashboard.refetch(), configuration.refetch(), rules.refetch()]);
        }}
      />
    );
  const stats = dashboard.data;
  return (
    <main className="portal-content mc-page mc-page--portal">
      <header className="welcome-row">
        <div>
          <p className="eyebrow">PLATFORM-OWNED VALUE</p>
          <h1>Mero Credits control centre</h1>
          <p>
            Govern one Mero Credits wallet system independently from Business Owner Reward Points and
            Mero Tier Points.
          </p>
        </div>
        <span
          className={`mc-regulatory-badge ${configuration.data.regulatoryState === "APPROVED" ? "is-approved" : ""}`}
        >
          <ShieldCheck /> {configuration.data.regulatoryState}
        </span>
      </header>
      <div className="mc-admin-stats">
        <article>
          <WalletCards />
          <div>
            <small>Outstanding liability</small>
            <strong>{credits(stats.wallets.outstandingMinor)}</strong>
            <p>{String(stats.wallets.walletCount)} wallets</p>
          </div>
        </article>
        <article>
          <Gift />
          <div>
            <small>Promotional outstanding</small>
            <strong>{credits(stats.wallets.promotionalOutstandingMinor)}</strong>
            <p>{credits(stats.ledger.issuedMinor)} total issued</p>
          </div>
        </article>
        <article>
          <Landmark />
          <div>
            <small>Settlement payable</small>
            <strong>{credits(stats.settlements.payableMinor)}</strong>
            <p>{String(stats.settlements.settlementCount)} entries</p>
          </div>
        </article>
        <article>
          <AlertTriangle />
          <div>
            <small>Open risk events</small>
            <strong>{String(stats.risks.openRiskEvents)}</strong>
            <p>{String(stats.rules.activeRules)} active rules</p>
          </div>
        </article>
      </div>
      <section className="mc-panel">
        <div className="mc-panel__heading">
          <div>
            <p className="eyebrow">LAUNCH GATES & LIMITS</p>
            <h2>Governed configuration</h2>
            <p>
              Changes are audited. Enabling regulated value movement requires an APPROVED state and
              approval reference.
            </p>
          </div>
          <ShieldCheck />
        </div>
        <MeroCreditConfigurationForm configuration={configuration.data} />
      </section>
      <section className="mc-panel">
        <div className="mc-panel__heading">
          <div>
            <p className="eyebrow">VERSIONED EARNING ENGINE</p>
            <h2>Mero Credit rules</h2>
            <p>
              Business Owners cannot edit these rules. Draft, activate, pause, and budget platform-owned
              grants here.
            </p>
          </div>
          <Sparkles />
        </div>
        <Rules rules={rules.data} />
        <details className="mc-builder">
          <summary>
            <Plus /> Create a new rule
          </summary>
          <RuleBuilder />
        </details>
      </section>
      <section className="mc-panel">
        <div className="mc-panel__heading">
          <div>
            <p className="eyebrow">SETTLEMENTS</p>
            <h2>Merchant receivables</h2>
            <p>Cross-merchant redemptions create append-only settlement entries.</p>
          </div>
          <Landmark />
        </div>
        {(settlements.data ?? []).length ? (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Merchant</th>
                  <th>Gross</th>
                  <th>Net</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {(settlements.data ?? []).map((item) => (
                  <tr key={item.publicId}>
                    <td>
                      <strong>{item.tenantName}</strong>
                    </td>
                    <td>{credits(item.grossAmountMinor)}</td>
                    <td>{credits(item.netAmountMinor)}</td>
                    <td>
                      <span className={`status-pill status-pill--${item.status.toLowerCase()}`}>
                        {item.status}
                      </span>
                    </td>
                    <td>{date(item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mc-empty">
            <Landmark />
            <strong>No settlement entries</strong>
            <p>This remains empty while global redemption is blocked.</p>
          </div>
        )}
      </section>
    </main>
  );
}
